// @ts-nocheck
import { useGetStatsOverview, useGetDailyStats, useGetCampaignStats } from "../lib/api-client-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { StatCard } from "../components/StatCard";
import { PageHeader } from "../components/layout/PageHeader";
import { useLang } from "../lib/lang";

const PIE_COLORS = ["#1171ef", "#2dce89", "#f5365c", "#11cdef"];

export function Dashboard() {
  const { t } = useLang();
  const d = t.dashboard;

  const { data: overview, isLoading } = useGetStatsOverview();
  const { data: dailyStats } = useGetDailyStats();
  const { data: campaignStats } = useGetCampaignStats();

  const chartData = dailyStats?.map((s) => ({
    date: new Date(s.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    sent: s.sent,
    delivered: s.delivered,
    failed: s.failed,
  })) ?? [];

  const pieData = [
    { name: d.delivered, value: overview?.totalMessagesDelivered ?? 80 },
    { name: d.sent, value: Math.max(0, (overview?.totalMessagesSent ?? 120) - (overview?.totalMessagesDelivered ?? 80)) },
    { name: d.failed, value: overview?.totalMessagesFailed ?? 10 },
    { name: d.read, value: overview?.totalMessagesRead ?? 30 },
  ].filter(item => item.value > 0);

  const autoRepliesData = [
    { name: d.days.Mon, replies: 4 },
    { name: d.days.Tue, replies: 7 },
    { name: d.days.Wed, replies: 3 },
    { name: d.days.Thu, replies: 9 },
    { name: d.days.Fri, replies: 6 },
    { name: d.days.Sat, replies: 2 },
    { name: d.days.Sun, replies: 5 },
  ];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-40" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl shadow-sm border border-[#e9ecef]" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={d.title}
        actions={[
          { label: d.createDevice, icon: "fas fa-plus", onClick: () => window.location.href = "/devices" },
          { label: d.sendMessage, icon: "fas fa-paper-plane", onClick: () => window.location.href = "/single-send" },
        ]}
      />

      <div className="px-6 pb-6 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={d.totalDevices} value={overview?.totalDevices ?? 0} iconClass="fas fa-server" gradient="primary" />
          <StatCard label={d.totalMessages} value={(overview?.totalMessagesSent ?? 0).toLocaleString()} iconClass="fas fa-paper-plane" gradient="info" />
          <StatCard label={d.pendingSchedules} value={0} iconClass="fas fa-calendar-alt" gradient="warning" />
          <StatCard label={d.totalContacts} value={overview?.totalContacts ?? 0} iconClass="fas fa-address-book" gradient="success" />
        </div>

        {/* Charts row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Messages Transaction */}
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
            <div className="px-5 py-4 border-b border-[#e9ecef] flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#32325d]">{d.messagesTransaction}</h4>
              <select className="text-xs border border-[#e9ecef] rounded px-2 py-1 text-[#525f7f] bg-white">
                <option>{d.last7Days}</option>
                <option>{d.today}</option>
                <option>{d.last30Days}</option>
              </select>
            </div>
            <div className="p-4">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8898aa" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8898aa" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9ecef" }} />
                  <Line type="monotone" dataKey="sent" stroke="#1171ef" strokeWidth={2} dot={false} name={d.sent} />
                  <Line type="monotone" dataKey="delivered" stroke="#2dce89" strokeWidth={2} dot={false} name={d.delivered} />
                  <Line type="monotone" dataKey="failed" stroke="#f5365c" strokeWidth={2} dot={false} name={d.failed} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Automatic Replies */}
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
            <div className="px-5 py-4 border-b border-[#e9ecef] flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#32325d]">{d.automaticReplies}</h4>
              <select className="text-xs border border-[#e9ecef] rounded px-2 py-1 text-[#525f7f] bg-white">
                <option>{d.last7Days}</option>
                <option>{d.today}</option>
                <option>{d.last30Days}</option>
              </select>
            </div>
            <div className="p-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={autoRepliesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8898aa" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#8898aa" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9ecef" }} />
                  <Bar dataKey="replies" fill="#1171ef" radius={[4, 4, 0, 0]} name={d.autoReplies} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Charts row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Messages Doughnut */}
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
            <div className="px-5 py-4 border-b border-[#e9ecef] flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#32325d]">{d.messages}</h4>
              <select className="text-xs border border-[#e9ecef] rounded px-2 py-1 text-[#525f7f] bg-white">
                <option>{d.last7Days}</option>
                <option>{d.today}</option>
                <option>{d.last30Days}</option>
              </select>
            </div>
            <div className="p-4 flex items-center justify-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e9ecef" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {pieData.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-[#525f7f] text-xs">{entry.name}</span>
                    <span className="font-semibold text-[#32325d] text-xs ms-auto">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Devices Statistics */}
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
            <div className="px-5 py-4 border-b border-[#e9ecef]">
              <h4 className="text-sm font-semibold text-[#32325d]">{d.devicesStatistics}</h4>
            </div>
            <div className="p-4">
              {campaignStats && campaignStats.length > 0 ? (
                <div className="space-y-3">
                  {campaignStats.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-[#f6f9fc] last:border-0">
                      <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {(c.name ?? "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#32325d] truncate">{c.name}</p>
                        <p className="text-xs text-[#8898aa]">{c.sent} {d.sent.toLowerCase()} · {c.delivered} {d.delivered.toLowerCase()}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.sent > 0 ? "bg-[#d4edda] text-[#155724]" : "bg-[#f8d7da] text-[#721c24]"}`}>
                        {c.sent > 0 ? Math.round((c.delivered / c.sent) * 100) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#8898aa]">
                  <i className="fas fa-mobile-alt text-2xl mb-2 block" />
                  <p className="text-sm">{d.noDevices}</p>
                  <a href="/devices" className="text-[#1171ef] text-xs mt-1 inline-block hover:underline">{d.addDevice}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
